// Месяцы и объекты расчёта: таблица там, где она помещается в окно, и карточки там, где нет.
//
// ## Почему выбор сделан здесь, а не стилями
//
// Колонок в таблице от одиннадцати до пятнадцати, и сколько их у этого человека, знают только
// данные: обучение, штраф, премия и прочие удержания появляются, если у кого-то из блоков они
// не нулевые. Окно при этом бывает любым: планшет в портрете (744), ландшафт (1133), ноутбук
// (1280), монитор. Ни один порог ширины не угадает, влезет ли таблица этого человека в это окно, и
// тогда либо карточки без нужды на всём, что уже, либо колонка за краем, как было на 1133 и
// 744 до 01.10.2026. Поэтому окно сначала решает грубо, а потом таблицу МЕРЯЮТ.
//
// ## Как решается
//
// 1. Окно уже 900 точек (портрет планшета): карточки сразу, мерить нечего.
// 2. Иначе рисуется таблица, и до первой отрисовки измеряется, влезла ли она в свой контейнер. Не
//    влезла — запоминается её ширина, и вместо неё рисуются карточки. Измерение идёт в `layout`-
//    эффекте, то есть до того, как кадр попадёт на экран: человек не видит, как таблица
//    «мигнула» и пропала.
// 3. Контейнер вырос до запомненной ширины (повернули планшет, растянули окно) — снова таблица,
//    и снова проверка: если и теперь не влезла, вернутся карточки в том же кадре.
//
// Измеряется ширина самого контейнера, а не окна: полоса разделов и колонки списка отнимают у
// содержимого разное, и «окно 1280» бывает и 1000 точек таблице, и 600.
//
// ## Что не замеряется
//
// В проверках без разметки (jsdom) ширина всех элементов нулевая, и таблица считается влезшей:
// режим карточек там задаётся только шириной окна. Вживую же, на планшете, цену этого решения
// платит одно: на первой отрисовке при нехватке места срабатывает ещё один проход.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import type {
  PayslipPeriodBlock,
  PayslipPeriodGap,
  PayslipPeriodTotals,
} from '@/shared/api/endpoints/payslip';
import { useСреда } from '@/shared/среда';

import { КарточкиБлоков } from './КарточкиБлоков';
import { ТаблицаБлоков, видимыеКолонки, ключМеста } from './ТаблицаБлоков';

/** Пробелы, где часов нет, а название места уже сказано над блоками: замечание про часы там повтор. */
const ПРОБЕЛЫ_С_ЧАСАМИ: ReadonlySet<PayslipPeriodGap['kind']> = new Set([
  'no_timesheet',
  'hours_not_in_payroll',
]);

/** Допуск на округление: ширина содержимого и ширина контейнера делятся на доли точки. */
const ДОПУСК = 1;

export function БлокиРасчёта({
  блоки,
  итог,
  подпись,
  пробелы,
}: {
  блоки: readonly PayslipPeriodBlock[];
  итог: PayslipPeriodTotals;
  /** Для чтения с экрана: какому юрлицу принадлежит таблица. */
  подпись: string;
  /** Пробелы документа: по ним замечание «часы не проставлены» не повторяется под блоком. */
  пробелы: readonly PayslipPeriodGap[];
}) {
  const { ширина } = useСреда();
  const окноУзкое = ширина === 'мало' || ширина === 'узко';
  const контейнер = useRef<HTMLDivElement>(null);

  const часыУжеНазваны = useMemo(
    () =>
      new Set(
        пробелы
          .filter((пробел) => ПРОБЕЛЫ_С_ЧАСАМИ.has(пробел.kind))
          .map((пробел) => ключМеста(пробел.site_id, пробел.period_year, пробел.period_month)),
      ),
    [пробелы],
  );

  // Форма данных: другие колонки или другое число блоков означают другую ширину таблицы, и прежнее
  // «не влезла» к ним уже не относится.
  const форма = `${видимыеКолонки(блоки)
    .map((колонка) => колонка.ключ)
    .join(',')}|${блоки.length}`;
  const [тесно, setТесно] = useState<{ форма: string; нужно: number } | null>(null);
  const нужно = тесно !== null && тесно.форма === форма ? тесно.нужно : null;
  const карточки = окноУзкое || нужно !== null;

  useLayoutEffect(() => {
    const узел = контейнер.current;
    if (узел === null || окноУзкое) {
      return undefined;
    }
    const проверить = () => {
      if (нужно !== null) {
        // Карточки из-за тесноты: вернуться к таблице, когда места стало столько, сколько ей нужно.
        if (узел.clientWidth >= нужно) {
          setТесно(null);
        }
        return;
      }
      if (узел.scrollWidth > узел.clientWidth + ДОПУСК) {
        setТесно({ форма, нужно: узел.scrollWidth });
      }
    };
    проверить();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const наблюдатель = new ResizeObserver(проверить);
    наблюдатель.observe(узел);
    // И саму таблицу: пока загружается шрифт, её колонки меняют ширину, а контейнер остаётся прежним.
    if (узел.firstElementChild !== null && нужно === null) {
      наблюдатель.observe(узел.firstElementChild);
    }
    return () => наблюдатель.disconnect();
  }, [окноУзкое, нужно, форма]);

  return (
    // `overflow-x-auto` остаётся последней страховкой: если замер не удался (нет наблюдателя за
    // размером), таблицу хотя бы можно листать, а не она режется по краю блока.
    <div ref={контейнер} className='overflow-x-auto'>
      {карточки ? (
        <КарточкиБлоков
          блоки={блоки}
          итог={итог}
          подпись={подпись}
          часыУжеНазваны={часыУжеНазваны}
        />
      ) : (
        <ТаблицаБлоков
          блоки={блоки}
          итог={итог}
          подпись={подпись}
          часыУжеНазваны={часыУжеНазваны}
        />
      )}
    </div>
  );
}
